import React, { useState, useEffect, useCallback } from 'react';
import { TodoRow } from './TodayView';
import ItemDetailModal from './ItemDetailModal';
import { IconDecision, IconArtifact, IconLink } from './Icons';
import { updateWorkspaceArtifact } from '../utils/artifacts';
import './WorkRail.css';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || '';

// The small leading tag that distinguishes each item kind (mirrors LandingView's
// tagFor so the lightbox reads the same everywhere).
function decisionTag(d) {
  return { label: d.status || 'active', cls: `status-${d.status || 'active'}` };
}
function artifactTag(a) {
  const t = a.type || 'note';
  return {
    label: t === 'shortlist' ? 'short list' : t,
    cls: t === 'shortlist' ? 'kind-shortlist' : 'type',
  };
}

// Normalise a landing row into the shape ItemDetailModal reads. `editable`
// marks the rows the lightbox may edit in place: real artefacts only — short
// lists live in their own table and are curated item-by-item in the thread, and
// decisions/todos aren't free-text edits.
function toDetail(it, tag, { editable = false } = {}) {
  return {
    id: it.id,
    title: it.title,
    body: it.body || '',
    roomId: it.roomId,
    roomName: it.roomName,
    by: it.by,
    at: it.at,
    tagLabel: tag.label,
    tagClass: tag.cls,
    editable,
  };
}

function isEditableArtifact(a) {
  return !!a && a.type !== 'shortlist';
}

// Direction B desktop three-pane "Work rail": summaries of Decisions + Todos +
// Artefacts always visible beside the thread. Pure client composition over the
// existing token-free landing/todos rollups (no new endpoints).
//
// Selecting a summary row opens the item "to the fore" in a lightbox
// (ItemDetailModal); the trailing link icon jumps straight to the item's
// provenance (its source thread) instead.
export default function WorkRail({ workspaceId, onOpenThread, refreshKey, canEdit = false }) {
  const [decisions, setDecisions] = useState(null);
  const [artifacts, setArtifacts] = useState(null);
  const [todos, setTodos] = useState(null);
  const [detailItem, setDetailItem] = useState(null);

  useEffect(() => {
    if (!workspaceId) {
      setDecisions(null);
      setArtifacts(null);
      setTodos(null);
      return undefined;
    }
    const token = localStorage.getItem('symphonate-token');
    if (!token) return undefined;
    let cancelled = false;
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    fetch(`${SERVER_URL}/api/workspaces/${workspaceId}/landing`, auth)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled) return;
        setDecisions(Array.isArray(data?.decisions) ? data.decisions : []);
        setArtifacts(Array.isArray(data?.artifacts) ? data.artifacts : []);
      })
      .catch(() => {});
    fetch(`${SERVER_URL}/api/workspaces/${workspaceId}/todos`, auth)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled) setTodos(Array.isArray(data?.todos) ? data.todos : []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [workspaceId, refreshKey]);

  // Optimistic toggle, then PATCH (same path the Lobby uses); the thread's live
  // todos-updated broadcast keeps everyone else in sync.
  const toggleTodo = useCallback(
    (todo) => {
      if (!workspaceId || !todo?.id) return;
      const token = localStorage.getItem('symphonate-token');
      setTodos((prev) =>
        (prev || []).map((t) => (t.id === todo.id ? { ...t, completed: !t.completed } : t)),
      );
      fetch(`${SERVER_URL}/api/workspaces/${workspaceId}/todos/${todo.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    },
    [workspaceId],
  );

  const openTodoDetail = useCallback((todo) => {
    setDetailItem({
      id: todo.id,
      title: todo.text,
      body: todo.text || '',
      roomId: todo.roomId,
      roomName: todo.roomName,
      by: todo.createdBy,
      at: todo.createdAt,
      tagLabel: todo.completed ? 'done' : 'todo',
      tagClass: 'kind-todo',
    });
  }, []);

  // Save an artefact edited in the lightbox, then reflect it in the rail (and
  // in the open lightbox) without refetching the whole rollup. Everyone in the
  // artefact's thread gets the server's `artifacts-updated` broadcast.
  const saveArtifact = useCallback(
    async (item, { title, content }) => {
      const updated = await updateWorkspaceArtifact(workspaceId, item.id, { title, content });
      const at = updated?.updatedAt || new Date().toISOString();
      setArtifacts((prev) =>
        (prev || []).map((a) => (a.id === item.id ? { ...a, title, body: content, at } : a)),
      );
      setDetailItem((prev) =>
        prev && prev.id === item.id ? { ...prev, title, body: content } : prev,
      );
    },
    [workspaceId],
  );

  const openTodos = (todos || []).filter((t) => !t.completed);

  // One summary row: the body opens the lightbox, the link icon opens the
  // provenance thread.
  const summaryRow = (item, tag, icon, opts) => (
    <div className="work-rail-row" key={`${tag.cls}-${item.id}`}>
      <button
        type="button"
        className="work-rail-summary"
        onClick={() => setDetailItem(toDetail(item, tag, opts))}
        title="Open to read"
      >
        <span className="work-rail-summary-icon" aria-hidden="true">
          {icon}
        </span>
        <span className="work-rail-summary-body">
          <span className="work-rail-summary-text">{item.title}</span>
          <span className="work-rail-summary-meta">
            {[item.roomName, tag.label].filter(Boolean).join(' · ')}
          </span>
        </span>
      </button>
      {item.roomId && onOpenThread && (
        <button
          type="button"
          className="work-rail-provenance"
          onClick={() => onOpenThread(item.roomId)}
          aria-label={`Go to ${item.roomName || 'source thread'}`}
          title={`Go to ${item.roomName || 'source thread'}`}
        >
          <IconLink size={14} />
        </button>
      )}
    </div>
  );

  return (
    <aside className="work-rail" aria-label="Decisions, todos and artefacts">
      <div className="work-rail-eyebrow">Decisions</div>
      <div className="work-rail-card">
        {decisions === null ? (
          <p className="work-rail-empty">Loading…</p>
        ) : decisions.length === 0 ? (
          <p className="work-rail-empty">No decisions yet.</p>
        ) : (
          decisions
            .slice(0, 12)
            .map((d) => summaryRow(d, decisionTag(d), <IconDecision size={15} />))
        )}
      </div>

      <div className="work-rail-eyebrow">Todos</div>
      <div className="work-rail-card">
        {todos === null ? (
          <p className="work-rail-empty">Loading…</p>
        ) : openTodos.length === 0 ? (
          <p className="work-rail-empty">Nothing open.</p>
        ) : (
          openTodos
            .slice(0, 15)
            .map((td) => (
              <TodoRow
                key={td.id}
                todo={td}
                onToggle={toggleTodo}
                onOpenThread={onOpenThread}
                onOpenDetail={openTodoDetail}
              />
            ))
        )}
      </div>

      <div className="work-rail-eyebrow">Artefacts</div>
      <div className="work-rail-card">
        {artifacts === null ? (
          <p className="work-rail-empty">Loading…</p>
        ) : artifacts.length === 0 ? (
          <p className="work-rail-empty">No artefacts yet.</p>
        ) : (
          artifacts.slice(0, 12).map((a) =>
            summaryRow(a, artifactTag(a), <IconArtifact size={15} />, {
              editable: canEdit && isEditableArtifact(a),
            }),
          )
        )}
      </div>

      <ItemDetailModal
        item={detailItem}
        onClose={() => setDetailItem(null)}
        onSave={saveArtifact}
        onJoinThread={(roomId) => {
          setDetailItem(null);
          if (onOpenThread) onOpenThread(roomId);
        }}
      />
    </aside>
  );
}
